import { z } from 'zod';

// ─── Event payload: eval.matrix.requested ────────────────────────────────────

/**
 * One column of the eval matrix: a specific (model × prompt version) combination.
 *
 * `is_prod_baseline` (D10) — when true, this column is the "Production (live)" baseline:
 * the handler READS existing `categories_i18n.<field>` (target_lang) instead of generating
 * (zero LLM calls, cost $0, tokens 0, latency 0; empty prod field → cell status 'skip').
 * `ai_model_id`/`prompt_id` still point at the current `is_current` prompt for informational
 * labelling. Only non-baseline combos generate fresh output.
 */
export const evalMatrixComboSchema = z.object({
  ai_model_id: z.number().int().positive(),
  prompt_id: z.number().int().positive(),
  is_prod_baseline: z.boolean().optional().default(false),
});

export type EvalMatrixCombo = z.infer<typeof evalMatrixComboSchema>;

/**
 * Payload for the `eval.matrix.requested` event.
 *
 * Emitted by the admin UI action [Run matrix], consumed by the eval-matrix worker handler.
 * - `target_lang`   — BCP-47 language tag for the generation run (default: 'pl').
 * - `category_ids`  — row sample selected by the user (Decision D1: user-picked, not all).
 * - `fields`        — subset of CATEGORY_GENERATABLE_FIELDS to evaluate (tabs in the matrix).
 * - `combos`        — columns: each entry pins a specific (ai_model_id × immutable prompt_id).
 */
export const evalMatrixRequestedPayloadSchema = z.object({
  target_lang: z.string().min(2).max(10),
  category_ids: z.array(z.number().int().positive()).min(1),
  fields: z.array(z.string().min(1)).min(1),
  combos: z.array(evalMatrixComboSchema).min(1),
});

export type EvalMatrixRequestedPayload = z.infer<typeof evalMatrixRequestedPayloadSchema>;

// ─── Event payload: eval.matrix.progress ─────────────────────────────────────

/**
 * Progress heartbeat emitted by the eval-matrix handler after each cell/batch.
 * Mirrors the bulk_ai.batch.done shape for UI parity (done_cells / total_cells).
 */
export const evalMatrixProgressPayloadSchema = z.object({
  run_id: z.number().int().positive(),
  done_cells: z.number().int().nonnegative(),
  total_cells: z.number().int().nonnegative(),
  cost_usd: z.number().nonnegative(),
  cost_estimated: z.boolean().optional(),
  cost_provenance: z.enum(['billed', 'estimate', 'unknown']).optional(),
});

export type EvalMatrixProgressPayload = z.infer<typeof evalMatrixProgressPayloadSchema>;

// ─── Event payload: eval.matrix.done ─────────────────────────────────────────

export const evalMatrixDonePayloadSchema = z.object({
  run_id: z.number().int().positive(),
  total_cells: z.number().int().nonnegative(),
  done_cells: z.number().int().nonnegative(),
  failed_cells: z.number().int().nonnegative(),
  skipped_cells: z.number().int().nonnegative(),
  cost_usd: z.number().nonnegative(),
  cost_estimated: z.boolean().optional(),
  cost_provenance: z.enum(['billed', 'estimate', 'unknown']).optional(),
  duration_ms: z.number().int().nonnegative(),
});

export type EvalMatrixDonePayload = z.infer<typeof evalMatrixDonePayloadSchema>;

// ─── DTO: eval_runs row ───────────────────────────────────────────────────────

/**
 * DTO mirroring the `eval_runs` table row (read-side, from Supabase query).
 * The `output` JSONB column does not exist on eval_runs; permissive typing
 * applies only to `eval_cells.output` (see EvalCellRow below).
 */
export interface EvalRunRow {
  id: number;
  correlation_id: string;
  kind: string;
  target_lang: string;
  category_ids: number[];
  fields: string[];
  status: 'pending' | 'running' | 'done' | 'failed' | 'cancelled';
  total_cells: number;
  done_cells: number;
  cost_usd: number;
  cost_estimated?: boolean;
  cost_provenance?: 'billed' | 'estimate' | 'unknown';
  created_by: string | null;
  created_at: string;
  finished_at: string | null;
  error: string | null;
}

// ─── DTO: eval_combos row ─────────────────────────────────────────────────────

/**
 * DTO mirroring the `eval_combos` table row (one column of the matrix).
 * Stores a snapshot of model + prompt text at the time of the run for reproducibility.
 */
export interface EvalComboRow {
  id: number;
  run_id: number;
  ai_model_id: number;
  model_label: string;
  provider_code: string;
  prompt_id: number;
  prompt_version: number;
  system_template: string;
  user_template: string;
  col_index: number;
  /** D10: true = "Production (live)" baseline column (read-from-prod, no generation). */
  is_prod_baseline: boolean;
}

// ─── DTO: eval_cells row ─────────────────────────────────────────────────────

/**
 * DTO mirroring the `eval_cells` table row (one matrix cell: category × combo × field).
 *
 * `output` is JSONB — intentionally permissive (`unknown`) because each field
 * stores a different shape: plain string (seo_title/seo_h1/...), string[] (keywords),
 * or object[] (faq). Consumers narrow the type at the point of use.
 */
export interface EvalCellRow {
  id: number;
  run_id: number;
  combo_id: number;
  category_id: number;
  category_name: string | null;
  field: string;
  status: 'pending' | 'ok' | 'fail' | 'skip';
  output: unknown;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  cost_estimated?: boolean;
  cost_provenance?: 'billed' | 'estimate' | 'unknown';
  latency_ms: number;
  manual_score: number | null;
  error: string | null;
  created_at: string;
}
